import { hostPriceTotal } from "@workspace/booking-rules";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { formatMoneyWithCurrency } from "@/lib/formatMoney";
import { resolveDisplayCurrency } from "@workspace/booking-rules";
import { formatTripDate, formatTripTime } from "@/lib/trip-datetime";
import { getDistanceUnitPlural } from "@/lib/api";
import {
  adjustmentToBaseline,
  diffAdjustment,
  findSeedAdjustment,
  previousBaseline,
  type AdjustmentBaseline,
  type DiffField,
} from "@/lib/adjustment-diff";
import type { TripAdjustment } from "@/lib/bookings-api";

interface Props {
  adjustments: TripAdjustment[];
  loading?: boolean;
  /** Country code drives the delivery distance unit (mi vs km). */
  countryCode?: string | null;
  /** Locale for date/time formatting. */
  locale?: string;
  /** Trip currency for money rendering. Defaults to USD if
   *  omitted, but every caller in the codebase passes the live trip so
   *  GBP/EUR negotiations render with the correct symbol. */
  trip?: { billing_currency_code?: string | null; local_currency_code?: string | null } | null;
  /** Task #3126 — audience switch. "owner" keeps the original admin-style
   *  timeline (Host request #N / Guest request #N, dotted rail). "guest"
   *  renders friendlier offer cards on the renter's trip page. Defaults
   *  to "owner" so the existing host call site keeps rendering exactly
   *  as it did before. */
  viewer?: "owner" | "guest";
}

/**
 * Negotiation history (task #1680). Renders the seed proposal as
 * a "Booking request" anchor and every later proposal as a focused
 * before → after diff produced by `lib/adjustment-diff.ts`. Hides itself
 * when only the seed row exists (nothing has been negotiated yet).
 *
 * Task #3126: a new `viewer="guest"` variant renders the same data as
 * rounded offer cards with conversational copy for renters. The default
 * `viewer="owner"` variant is unchanged.
 */
export function NegotiationHistory({ adjustments, loading, countryCode, locale, trip, viewer = "owner" }: Props) {
  const { t } = useTranslation();
  // Task #5745 — negotiation rounds are price BREAKDOWNs, shown in the
  // car's LOCAL market (display) currency (label only; amounts unchanged).
  const negotiationCurrency = resolveDisplayCurrency(trip);

  const rowsAsc = adjustments.map(a => viewer === "owner" ? {
    ...a, payment_due: hostPriceTotal({ rentalFee: a.rental_fee, deliveryFee: a.delivery_fee, serviceFee: a.service_fee, insuranceFee: a.insurance_fee }) ?? NaN,
  } : a).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const seed = findSeedAdjustment(rowsAsc);
  const hasNegotiation = rowsAsc.some((r) => r.counter_of != null || (seed && r.id !== seed.id));

  if (loading && rowsAsc.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("trip.loading")}</p>;
  }
  if (!hasNegotiation) return null;

  const formatLocation = (b: AdjustmentBaseline): string => {
    if (!b.is_delivery) return t("trip.free_pickup", "Free pickup at car location");
    if (b.airport_code) return `${t("trip.delivery", "Delivery")} · ${b.airport_code}`;
    if (b.delivery_address) return `${t("trip.delivery", "Delivery")} · ${b.delivery_address}`;
    if (b.delivery_distance != null) {
      return `${t("trip.delivery", "Delivery")} · ${b.delivery_distance} ${getDistanceUnitPlural(countryCode)}`;
    }
    return t("trip.delivery", "Delivery");
  };

  const fmtDate = (iso: string) => formatTripDate(iso, { locale });
  const fmtTime = (iso: string | null) => (iso ? formatTripTime(iso, { locale }) : "");
  const fmtTotal = (n: number) => Number.isFinite(n) ? formatMoneyWithCurrency(n, negotiationCurrency) : "—";

  // Task #3018 — mirror the chat's ProposalCard lapse check: a row
  // whose stored status is still `pending` but whose `expires_at` is
  // in the past is effectively Expired. Without this the overview
  // disagreed with the chat for the window between expiry and the
  // inngest cron stamping `expired_at` / flipping `status`.
  const statusStyle = (status: string, expiresAt: string | null | undefined) => {
    const expiredByClock =
      status === "pending" && !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
    if (expiredByClock) return { label: t("trip.status_expired"), cls: "bg-gray-100 text-gray-600" };
    return status === "accepted" ? { label: t("trip.status_accepted"), cls: "bg-brand-tint text-brand" }
      : status === "declined" ? { label: t("trip.status_declined"), cls: "bg-red-100 text-red-700" }
      : status === "superseded" ? { label: t("trip.status_superseded"), cls: "bg-gray-100 text-gray-600" }
      // Task #2405: cancelled = proposer withdrew their own pending
      // request. Render with the same neutral styling as superseded so
      // the chain reads "no longer actionable" at a glance.
      : status === "cancelled" ? { label: t("trip.status_cancelled"), cls: "bg-gray-100 text-gray-600" }
      // Task #3018 (follow-up): the server hard-stamps `status: "expired"`
      // once the inngest cron sets `expired_at`, and also returns the
      // discriminated `payment_failed` / `pricing_drift` terminals. Without
      // explicit branches these fell through to the `pending` default and
      // disagreed with the chat. Pending is now the LAST resort, not a
      // catch-all, so any future server-side status the client doesn't
      // recognise still renders neutrally instead of pretending to be live.
      : status === "expired" ? { label: t("trip.status_expired"), cls: "bg-gray-100 text-gray-600" }
      : status === "payment_failed" || status === "pricing_drift"
        ? { label: t("trip.status_expired"), cls: "bg-gray-100 text-gray-600" }
      : status === "pending" ? { label: t("trip.status_pending"), cls: "bg-amber-100 text-amber-800" }
      : { label: t("trip.status_superseded"), cls: "bg-gray-100 text-gray-600" };
  };

  if (viewer === "guest") {
    return (
      <GuestNegotiationCards
        rowsAsc={rowsAsc}
        seedId={seed?.id ?? null}
        locale={locale}
        t={t as unknown as TFunc}
        formatLocation={formatLocation}
        fmtDate={fmtDate}
        fmtTime={fmtTime}
        fmtTotal={fmtTotal}
        statusStyle={statusStyle}
      />
    );
  }

  // Render newest-first so the latest activity sits at the top of the
  // timeline — that's where the reviewer's eye lands and it matches
  // how chat / inbox feeds order. The data model stays chronological
  // (rowsAsc) because previousBaseline + the accepted-outcome scan
  // both need ascending order to walk the negotiation correctly.
  return (
    <ol className="relative space-y-4 border-l border-border pl-5">
      {rowsAsc.map((_a, displayI) => {
        const i = rowsAsc.length - 1 - displayI;
        const a = rowsAsc[i];
        const isSeed = !!seed && a.id === seed.id;
        const when = new Date(a.created_at).toLocaleString(locale, {
          month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
        const style = statusStyle(a.status, a.expires_at);
        const who = a.suggested_by_role === "host" ? t("trip.role_host") : t("trip.role_guest");

        let diffs: DiffField[] = [];
        if (!isSeed) {
          const prev = previousBaseline(rowsAsc, i);
          const next = adjustmentToBaseline(a);
          diffs = diffAdjustment(prev, next, {
            formatDate: fmtDate,
            formatTime: fmtTime,
            formatTotal: fmtTotal,
            formatLocation,
          });
        }

        return (
          <li key={a.id} className="relative">
            <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand" />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {isSeed ? t("trip.booking_request") : t("trip.proposal_who_id", { who, id: a.id })}
              </span>
              {!isSeed && (
                <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", style.cls)}>
                  {style.label}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{when}</span>
            </div>
            {isSeed ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {fmtDate(a.start_date)} – {fmtDate(a.end_date)}
                {a.pickup_at && a.dropoff_at && ` · ${fmtTime(a.pickup_at)} – ${fmtTime(a.dropoff_at)}`}
                {" · "}
                {viewer === "owner" ? `${t("pricing.host_basis_total", "Booking price (host basis)")}: ` : ""}{a.payment_due == null ? "—" : fmtTotal(Number(a.payment_due))}
              </p>
            ) : diffs.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">{t("trip.diff_no_changes", "No changes from previous terms")}</p>
            ) : (
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {diffs.map((d) => (
                  <li key={d.field}>
                    <span className="font-medium text-foreground">{viewer === "owner" && d.field === "total" ? t("pricing.host_basis_total", "Booking price (host basis)") : t(`trip.diff_${d.field}`)}:</span>{" "}
                    {t("trip.diff_before_after", { before: d.before, after: d.after })}
                  </li>
                ))}
              </ul>
            )}
            {viewer === "owner" && Number(a.host_insurance_fee ?? 0) > 0 && (
              <div className="mt-1 text-xs text-muted-foreground" data-testid="negotiation-host-insurance">
                <p>
                  {t("money_breakdown.host_insurance_deduction", "Host insurance")}
                  {a.host_insurance_policy_snapshot?.package_name ? ` · ${a.host_insurance_policy_snapshot.package_name}` : ""}
                  {": −"}
                  {fmtTotal(Number(a.host_insurance_fee))}
                </p>
                {a.host_insurance_policy_snapshot && (
                  <p>
                    {a.host_insurance_policy_snapshot.state_code} · {t("money_breakdown.insured_value", "Insured value")}{" "}
                    {fmtTotal(a.host_insurance_policy_snapshot.insured_value)}
                  </p>
                )}
                {a.owner_earn != null && (
                  <p className="font-medium text-foreground">
                    {t("money_breakdown.your_earnings")} {fmtTotal(Number(a.owner_earn))}
                  </p>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Task #3126 — guest offer-card variant
// ---------------------------------------------------------------------------

type StatusStyle = { label: string; cls: string };
// Loose translator signature — accepts i18next's `t` without dragging in
// its overload soup. The guest cards only need `t(key)` and `t(key, fallback)`.
type TFunc = (key: string, opts?: unknown) => string;

interface GuestCardsProps {
  rowsAsc: TripAdjustment[];
  seedId: number | null;
  locale: string | undefined;
  t: TFunc;
  formatLocation: (b: AdjustmentBaseline) => string;
  fmtDate: (iso: string) => string;
  fmtTime: (iso: string | null) => string;
  fmtTotal: (n: number) => string;
  statusStyle: (status: string, expiresAt: string | null | undefined) => StatusStyle;
}

/** Effective status for a row, mirroring `statusStyle`'s clock-lapse
 *  check so accent/dim decisions agree with the visible pill. */
function effectiveStatus(status: string, expiresAt: string | null | undefined): string {
  if (status === "pending" && !!expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    return "expired";
  }
  return status;
}

/** Map effective status → left accent border class. Accepted gets a strong
 *  green; declined gets red; pending/active gets amber; everything else
 *  (expired, superseded, cancelled, payment_failed, pricing_drift, …)
 *  renders muted grey. */
function accentClass(effective: string): string {
  if (effective === "accepted") return "border-l-brand";
  if (effective === "declined") return "border-l-red-500";
  if (effective === "pending") return "border-l-amber-500";
  return "border-l-gray-300";
}

/** Conversational title for a guest offer card. */
function guestCardTitle(args: {
  t: TFunc;
  isSeed: boolean;
  isAcceptedOutcome: boolean;
  status: string;
  role: "host" | "guest";
}): string {
  const { t, isSeed, isAcceptedOutcome, status, role } = args;
  if (isSeed) return t("trip.nego_you_requested");
  if (isAcceptedOutcome) return t("trip.nego_agreed_summary");
  if (status === "accepted") {
    return role === "host" ? t("trip.nego_host_accepted") : t("trip.nego_you_accepted");
  }
  if (status === "declined") {
    return role === "host" ? t("trip.nego_host_declined") : t("trip.nego_you_declined");
  }
  if (status === "expired") return t("trip.nego_offer_expired");
  if (status === "cancelled") {
    return role === "host" ? t("trip.nego_host_withdrew") : t("trip.nego_you_withdrew");
  }
  if (status === "payment_failed" || status === "pricing_drift" || status === "superseded") {
    return t("trip.nego_offer_expired");
  }
  // Pending / countered
  return role === "host" ? t("trip.nego_host_countered") : t("trip.nego_you_countered");
}

function GuestNegotiationCards({
  rowsAsc,
  seedId,
  locale,
  t,
  formatLocation,
  fmtDate,
  fmtTime,
  fmtTotal,
  statusStyle,
}: GuestCardsProps) {
  // Identify the most recent accepted row — it becomes the highlighted
  // "outcome" card at the end of the thread so the agreed terms are
  // unmistakable.
  let acceptedOutcomeIdx = -1;
  for (let i = rowsAsc.length - 1; i >= 0; i--) {
    if (rowsAsc[i].status === "accepted" && rowsAsc[i].id !== seedId) {
      acceptedOutcomeIdx = i;
      break;
    }
  }

  // Render newest-first (latest round at top). The acceptedOutcomeIdx
  // was computed against rowsAsc above, so we keep that as the data
  // model and only flip the render iteration — diffs vs. the previous
  // round and the outcome highlight both still resolve correctly.
  return (
    <ol className="space-y-3">
      {rowsAsc.map((_a, displayI) => {
        const i = rowsAsc.length - 1 - displayI;
        const a = rowsAsc[i];
        const isSeed = seedId != null && a.id === seedId;
        const isAcceptedOutcome = i === acceptedOutcomeIdx;
        const effective = effectiveStatus(a.status, a.expires_at);
        const style = statusStyle(a.status, a.expires_at);
        const title = guestCardTitle({
          t,
          isSeed,
          isAcceptedOutcome,
          status: effective,
          role: a.suggested_by_role,
        });
        const when = new Date(a.created_at).toLocaleString(locale, {
          month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });

        // De-emphasise rounds that are no longer actionable (but never
        // dim the accepted outcome — that's the headline card).
        const dim =
          !isAcceptedOutcome &&
          (effective === "expired" ||
            effective === "superseded" ||
            effective === "cancelled" ||
            effective === "payment_failed" ||
            effective === "pricing_drift" ||
            effective === "declined");

        const accent = isAcceptedOutcome ? "border-l-brand" : accentClass(effective);

        let diffs: DiffField[] = [];
        if (!isSeed) {
          const prev = previousBaseline(rowsAsc, i);
          const next = adjustmentToBaseline(a);
          diffs = diffAdjustment(prev, next, {
            formatDate: fmtDate,
            formatTime: fmtTime,
            formatTotal: fmtTotal,
            formatLocation,
          });
        }

        const cardCls = cn(
          "rounded-2xl border border-border border-l-4 bg-white p-4 shadow-sm transition-opacity",
          accent,
          isAcceptedOutcome && "bg-brand-tint/60 ring-1 ring-brand/20 shadow-md",
          dim && "opacity-70",
        );

        return (
          <li key={a.id} data-testid="guest-offer-card" className={cardCls}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{title}</span>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    style.cls,
                  )}
                >
                  {style.label}
                </span>
              </div>
              <span className="text-[11px] text-muted-foreground">{when}</span>
            </div>

            {isSeed || isAcceptedOutcome ? (
              <div className="mt-2 text-xs text-muted-foreground">
                <p>
                  {fmtDate(a.start_date)} – {fmtDate(a.end_date)}
                  {a.pickup_at && a.dropoff_at && ` · ${fmtTime(a.pickup_at)} – ${fmtTime(a.dropoff_at)}`}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">
                  {fmtTotal(Number(a.payment_due ?? 0))}
                </p>
              </div>
            ) : diffs.length === 0 ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("trip.nego_no_changes")}
              </p>
            ) : (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {diffs.map((d) => (
                  <li
                    key={d.field}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-foreground"
                  >
                    <span className="font-medium">{t(`trip.diff_${d.field}`)}:</span>
                    <span className="text-muted-foreground line-through">{d.before}</span>
                    <span aria-hidden>→</span>
                    <span className="font-semibold">{d.after}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}
